'use client'

import { useEffect, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'
import { toast } from 'sonner'
import { useAuthStore } from '@/lib/stores/auth'
import { useOrganizationStore } from '@/lib/stores/organization'

interface OrganizationLoaderProps {
  children: React.ReactNode
}

const MAX_RETRIES = 3
const RETRY_DELAY_MS = 2000

export function OrganizationLoader({ children }: OrganizationLoaderProps) {
  const pathname = usePathname()
  const isPublicRoute =
    pathname.startsWith('/open/') ||
    pathname.startsWith('/customer-portal/') ||
    pathname === '/login' ||
    pathname.startsWith('/login/')
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const isLoading = useAuthStore((s) => s.isLoading)
  const loadFromDB = useOrganizationStore((s) => s.loadFromDB)
  const dbLoaded = useOrganizationStore((s) => s._dbLoaded)
  const dbLoadError = useOrganizationStore((s) => s._dbLoadError)
  const loadingRef = useRef(false)
  const retryCount = useRef(0)
  const [retryTick, setRetryTick] = useState(0)
  const errorShown = useRef(false)

  useEffect(() => {
    if (dbLoaded) return
    if (loadingRef.current) return
    if (isLoading) return
    if (!isAuthenticated) return
    if (isPublicRoute) return
    if (retryCount.current >= MAX_RETRIES) return

    loadingRef.current = true
    retryCount.current += 1

    loadFromDB().finally(() => {
      loadingRef.current = false
      const { _dbLoaded } = useOrganizationStore.getState()
      if (!_dbLoaded) {
        if (retryCount.current < MAX_RETRIES) {
          setTimeout(() => setRetryTick((t) => t + 1), RETRY_DELAY_MS)
        } else {
          // エラーが設定されていない場合（401サイレントスキップ等）はトースト表示
          const { _dbLoadError: err } = useOrganizationStore.getState()
          if (!err) {
            toast.warning(
              'ユーザーリストの読み込みに失敗しました。ページを再読み込みしてください。'
            )
          }
        }
      }
    })
  }, [isAuthenticated, isLoading, loadFromDB, isPublicRoute, dbLoaded, retryTick])

  useEffect(() => {
    if (!dbLoadError || errorShown.current) return
    if (isPublicRoute) return
    errorShown.current = true
    toast.error(`組織データの読み込みに失敗しました: ${dbLoadError}`)
  }, [dbLoadError, isPublicRoute])

  return <>{children}</>
}
